import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Badge, Button } from 'antd';
import axios from 'axios';
import { uniqBy, upperCase } from 'lodash';
import moment from 'moment-timezone';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Clock, User } from 'phosphor-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  actionDimensionIds,
  ARTICLE_ACTION_TYPE,
  CONTENT_CATEGORY,
  CONTENT_TYPE,
  DEFAULT_ARTICLE_LIST_LIMIT,
  MATOMO_ACTION,
  MATOMO_CATEGORY,
  MATOMO_NAME,
  MEMBERS_ONLY,
  ROUTES,
  TRACK_VALUE
} from '../../../../common/constants';
import useDeviceInfo from '../../../../common/useDeviceInfo';
import useNetworkType from '../../../../common/useNetworkInfo';
import { getUserCommonDimensions } from '../../../../common/utils';
import { gaEvent } from '../../../../lib/gtag';
import { useApp } from '../../../../providers/AppContext';
import CustomAnchor from '../../../CustomAnchor';
import InView from '../../../InView';
import PremiumArticlesListLoading from '../../../skeleton/PremiumArticlesListLoading';

const formatArticleDate = (date) => {
  if (!date) return '';
  return moment(date).format('DD MMM YYYY').toUpperCase();
};

function PremiumArticlesList({
  sectionHeading,
  headerDescription,
  title,
  description,
  actionName,
  actionType,
  limit
}) {
  const {
    state: { currentUser }
  } = useApp();
  const { trackEvent } = useMatomo();
  const router = useRouter();
  const finalActionType = actionType || ARTICLE_ACTION_TYPE[0].value; // Default to LOAD_MORE for backward compatibility
  const finalActionName = actionName || ARTICLE_ACTION_TYPE[0].name;
  const initialArticles = []; // Articles are fetched via REST API, not from GraphQL
  const [articleData, setArticleData] = useState(initialArticles);
  const [isLoading, setLoading] = useState(initialArticles.length === 0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(initialArticles.length > 0 ? 2 : 1);
  const [hasMore, setHasMore] = useState(true);
  const device = useDeviceInfo();
  const networkType = useNetworkType();
  const observerRef = useRef(null);

  const getValidArticlesLimit = () => {
    if (typeof limit === 'number' && Number.isInteger(limit)) {
      return limit;
    }
    return DEFAULT_ARTICLE_LIST_LIMIT;
  };

  const displayLimit = getValidArticlesLimit();

  const formContentDimension = (id, articleTitle, link) => {
    const userDimensions = getUserCommonDimensions(
      currentUser,
      device,
      networkType
    );
    const contentObjectForVideo = [
      { id: actionDimensionIds.content_id, value: id },
      { id: actionDimensionIds.content_title, value: articleTitle },
      { id: actionDimensionIds.content_slug, value: link },
      { id: actionDimensionIds.content_category, value: CONTENT_CATEGORY },
      { id: actionDimensionIds.content_type, value: CONTENT_TYPE.ARTICLE },
      ...userDimensions
    ];
    return contentObjectForVideo;
  };

  const fetchArticles = useCallback(
    async (pageNum, isInitial = false) => {
      if (isInitial) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const url = `${
          process.env.NEXT_PUBLIC_ARTICLE_REST_API_URL
        }/main-server/api/v1/article?page=${pageNum}&per_page=${displayLimit}&is_premium=${true}`;
        const headers = {
          'x-api-key': process.env.NEXT_PUBLIC_ARTICLE_REST_API_HEADER_KEY
        };

        const response = await axios.get(url, { headers });
        const newArticles = response?.data?.articles || [];

        const responseData = response?.data || {};
        let hasMoreFromAPI = false;

        if (responseData?.hasMore !== undefined) {
          hasMoreFromAPI = responseData?.hasMore;
        } else if (responseData?.totalPages !== undefined) {
          hasMoreFromAPI = pageNum < responseData?.totalPages;
        } else if (responseData?.total !== undefined) {
          hasMoreFromAPI = pageNum * displayLimit < responseData?.total;
        } else {
          hasMoreFromAPI = newArticles.length === displayLimit;
        }

        setHasMore(hasMoreFromAPI);

        setArticleData((prev) => {
          const combined = [...prev, ...newArticles];
          return uniqBy(combined, 'id');
        });

        setLoading(false);
        setIsLoadingMore(false);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching articles:', err);
        setLoading(false);
        setIsLoadingMore(false);
        setHasMore(false);
      }
    },
    [displayLimit]
  );

  useEffect(() => {
    if (initialArticles.length === 0) {
      fetchArticles(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchArticles(nextPage, false);
    }
  }, [page, isLoadingMore, hasMore, fetchArticles]);

  const handleInView = useCallback(
    ({ inView }) => {
      // Only trigger infinite scroll when actionType is LOAD_MORE
      if (
        inView &&
        hasMore &&
        !isLoadingMore &&
        !isLoading &&
        finalActionType !== ARTICLE_ACTION_TYPE[1].value
      ) {
        handleLoadMore();
      }
    },
    [hasMore, isLoadingMore, isLoading, handleLoadMore, finalActionType]
  );

  const handleViewAll = useCallback(() => {
    trackEvent({
      category: MATOMO_CATEGORY.BUTTON,
      action: MATOMO_ACTION.CLICK,
      name: MATOMO_NAME.BUTTON_CLICK,
      customDimensions: [
        {
          id: actionDimensionIds.cta_title,
          value: finalActionName
        },
        {
          id: actionDimensionIds.page_source,
          value: router.asPath
        },
        ...getUserCommonDimensions(currentUser, device, networkType)
      ]
    });
    router.push(ROUTES.ARTICLES);
  }, [finalActionName, router, trackEvent, currentUser, device, networkType]);

  const handleAnalytics = (category, action, name, item) => {
    trackEvent({
      category,
      action,
      name,
      customDimensions: formContentDimension(item.id, item.title, item.link),
      value: TRACK_VALUE.SUCCESS
    });
    gaEvent({
      category,
      action: MATOMO_ACTION.VIEW_ARTICLE,
      name: JSON.stringify({
        action,
        userId: currentUser?.id ?? '',
        authorName: item?.author?.[0]?.author_name,
        href: item?.link
      }),
      value: TRACK_VALUE.SUCCESS
    });
  };

  return (
    <section className="premium-articles-list-section">
      {isLoading ? (
        <PremiumArticlesListLoading showFeatured />
      ) : (
        <div className="container">
          {(title || description) && (
            <div className="section-header">
              {title && sectionHeading ? <h3>{sectionHeading}</h3> : null}
              {description && headerDescription ? (
                <p>{headerDescription}</p>
              ) : null}
            </div>
          )}
          <div className="premium-articles-list">
            {articleData?.map((item, index) => {
              const isFirst = index === 0;
              return (
                <CustomAnchor
                  key={item?.id}
                  href={item?.link}
                  className={`premium-article-card ${
                    isFirst ? 'featured' : ''
                  }`}
                  onClick={() =>
                    handleAnalytics(
                      MATOMO_CATEGORY.VIEW_DETAIL,
                      MATOMO_ACTION.CLICK,
                      MATOMO_NAME.ARTICLE_CLICK,
                      item
                    )
                  }
                >
                  {isFirst ? (
                    <>
                      <div className="premium-article-content">
                        <Badge className="badge-md text-white-90 bg-n-900 premium-tag ml-4">
                          <span className="text-label-xs">{MEMBERS_ONLY}</span>
                        </Badge>
                        <h2 className="premium-article-title">{item?.title}</h2>
                        {item?.excerpt && (
                          <p
                            className="premium-article-description"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: item?.excerpt
                            }}
                          />
                        )}
                        <div className="premium-article-meta">
                          <div className="premium-article-author">
                            <User size={20} weight="regular" />
                            <span>
                              {upperCase(item?.author?.[0]?.author_name)}
                            </span>
                          </div>
                          <span className="premium-article-meta-separator">
                            |
                          </span>
                          <div className="premium-article-date">
                            <Clock size={20} weight="regular" />
                            <span>{formatArticleDate(item?.published)}</span>
                          </div>
                        </div>
                      </div>
                      {item?.main_image && (
                        <div className="premium-article-image">
                          <Image
                            src={item.main_image}
                            fill
                            alt={item?.main_image_attr || item?.title}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="premium-article-content">
                        <Badge className="badge-md text-white-90 bg-n-900 premium-tag ml-4">
                          <span className="text-label-xs">{MEMBERS_ONLY}</span>
                        </Badge>
                        <h2 className="premium-article-title">{item?.title}</h2>
                        {item?.excerpt && (
                          <p
                            className="premium-article-description"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: item?.excerpt
                            }}
                          />
                        )}
                        <div className="premium-article-meta">
                          <div className="premium-article-author">
                            <User size={20} weight="regular" />
                            <span>
                              {upperCase(item?.author?.[0]?.author_name)}
                            </span>
                          </div>
                          <span className="premium-article-meta-separator">
                            |
                          </span>
                          <div className="premium-article-date">
                            <Clock size={20} weight="regular" />
                            <span>{formatArticleDate(item?.published)}</span>
                          </div>
                        </div>
                      </div>
                      {item?.main_image && (
                        <div className="premium-article-image">
                          <Image
                            src={item.main_image}
                            fill
                            alt={item?.main_image_attr || item?.title}
                          />
                        </div>
                      )}
                    </>
                  )}
                </CustomAnchor>
              );
            })}
          </div>

          {articleData?.length > 0 &&
            (finalActionType !== ARTICLE_ACTION_TYPE[1].value ? (
              <>
                {/* Infinite scroll - only when actionType is LOAD_MORE and there are more articles */}
                {hasMore && !isLoadingMore && (
                  <InView
                    key={`inview-${page}-${articleData.length}`}
                    ref={observerRef}
                    onChange={handleInView}
                    rootMargin="200px"
                  />
                )}

                {/* Loading indicator for infinite scroll */}
                {isLoadingMore && (
                  <div className="premium-articles-loading">
                    <PremiumArticlesListLoading showFeatured={false} />
                  </div>
                )}
              </>
            ) : (
              <div className="article-view-btn text-center mt-20">
                <Button onClick={handleViewAll} type="primary">
                  {finalActionName}
                </Button>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}

PremiumArticlesList.Loading = function Loading() {
  return <PremiumArticlesListLoading />;
};

export default PremiumArticlesList;
